import { useAppAuth } from '../auth/appAuth';
import { useMemo } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';

// Development auth bypass
const DEV_BYPASS_AUTH = process.env.REACT_APP_BYPASS_AUTH === 'true';

export const useAxiosClient = () => {
  const { getAccessTokenSilently, loginWithRedirect, isAuthenticated } = useAppAuth();
  const navigate = useNavigate();
  const { orgId } = useParams<{ orgId: string }>();

  const axiosInstance = useMemo(() => {
    const instance = axios.create({
      baseURL: process.env.REACT_APP_API_SERVER_URL || 'https://127.0.0.1:5005',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add an interceptor to attach the access token
    instance.interceptors.request.use(
      async (config) => {
        // Dev bypass: skip token attachment if not authenticated
        if (DEV_BYPASS_AUTH && !isAuthenticated) {
          // No token needed - backend will also bypass auth
          console.log('🔓 Auth bypass: skipping token attachment');
        } else {
          // Normal flow: get and attach token
          try {
            const token = await getAccessTokenSilently();
            if (token && config.headers) {
              config.headers.Authorization = `Bearer ${token}`;
            }
          } catch (error: any) {
            // In bypass mode, token errors are OK (user just isn't logged in)
            if (DEV_BYPASS_AUTH) {
              console.log('🔓 Auth bypass: no token available, continuing without');
            } else {
              // Handle refresh token issues by forcing re-authentication
              if (error?.error === 'missing_refresh_token' || 
                  error?.message?.includes('Missing Refresh Token') ||
                  error?.message?.includes('refresh_token')) {
                console.warn('Refresh token issue detected, triggering re-authentication');

                try {
                  await loginWithRedirect();
                } catch (loginError) {
                  console.error('Failed to trigger login redirect after refresh token issue', loginError);
                  navigate('/login');
                }

                throw new Error('Authentication refresh required');
              }
              throw error;
            }
          }
        }

        // Skip org check for these endpoints
        const skipOrgCheck = [
          '/api/default-org',
          '/callback',
          '/api/available-orgs',
          '/api/admin/organizations',
          '/api/admin/organization-access',
          '/api/chat/test',
          '/api/chat/stream',
          '/api/gemini-lab/analysis',
          '/api/gemini-lab/analysis/filtered',
          '/api/gemini-lab/results',
          '/api/gemini-lab/taxonomy',
          '/api/gemini-lab/taxonomy/labels',
          '/api/gemini-lab/engagement-by-category',
          '/api/gemini-lab/brand-breakdown'
        ];
        if (skipOrgCheck.includes(config.url || '') || (config.url || '').startsWith('/api/admin/')) {
          return config;
        }

        // For all other endpoints, ensure we have a valid org parameter
        const currentOrgId = orgId;

        if (!currentOrgId || currentOrgId === '0') {
          // Redirect to default org if no org is selected
          navigate('/');
          throw new Error('No organization selected');
        }

        // Add org parameter to the API request
        const url = new URL(config.url || '', window.location.origin);
        url.searchParams.set('org', currentOrgId);
        config.url = url.pathname + url.search;

        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor to handle redirects and errors
    instance.interceptors.response.use(
      (response) => {
        // Check if it's a redirect response (status 307)
        if (response.status === 307 && response.data.redirect_url) {
          // Extract the query parameters from the redirect URL
          const queryString = response.data.redirect_url.split('?')[1];
          const currentPath = window.location.pathname;
          
          // Navigate to the new URL while preserving the current path
          navigate(`${currentPath}?${queryString}`);
          
          // Retry the original request with the new URL
          return instance.request({
            ...response.config,
            url: response.data.redirect_url,
          });
        }
        return response;
      },
      (error) => {
        // Handle auth errors
        if (error.response) {
          if (error.response.status === 401) {
            // Unauthorized - redirect to login
            navigate('/login');
          } else if (error.response.status === 403) {
            // Forbidden - check if it's a "no organization access" error
            const errorMessage = error.response.data?.error || '';
            if (errorMessage.includes('no organization access') || errorMessage.includes('User has no organization access')) {
              navigate('/no-access', { 
                state: { 
                  errorData: error.response.data 
                } 
              });
            }
          }
        }
        return Promise.reject(error);
      }
    );

    return instance;
  }, [getAccessTokenSilently, loginWithRedirect, navigate, orgId, isAuthenticated]);

  return axiosInstance;
}; 